import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/LanguageContext';
import {
  ArrowRight, Check,
  UtensilsCrossed, Coffee, Pizza, Music, IceCreamCone,
  Store, Shirt, Footprints, Watch, ShoppingBag,
  ShoppingCart, Croissant, Cake, Beef, Apple,
  Scissors, Sparkles, Flower2, Hand,
  Network, Building2, Layers, Truck, Users,
} from 'lucide-react';

const TAG_ICONS = {
  // Hostelería
  'Restaurantes': UtensilsCrossed,
  'Bares y cafeterías': Coffee,
  'Fast Food': Pizza,
  'Discotecas y pubs': Music,
  'Heladerías': IceCreamCone,
  // Retail
  'Comercios': Store,
  'Tiendas de ropa': Shirt,
  'Zapaterías': Footprints,
  'Accesorios': Watch,
  'Boutiques': ShoppingBag,
  // Alimentación
  'Supermercados': ShoppingCart,
  'Panaderías': Croissant,
  'Pastelerías': Cake,
  'Carnicerías': Beef,
  'Fruterías': Apple,
  // Belleza
  'Peluquerías': Scissors,
  'Centros de estética': Sparkles,
  'Spa': Flower2,
  'Nail Studio': Hand,
  'Barbería': Scissors,
  // Cadenas y Franquicias
  'Cadenas': Network,
  'Franquicias': Building2,
  'Multi-establecimiento': Layers,
  'Central de compras': Truck,
  'Grupos de restauración': Users,
};
import Navbar from '@/components/smartpos/Navbar';
import Footer from '@/components/smartpos/Footer';
import JsonLd from '@/components/smartpos/JsonLd';
import VideoPlayer from '@/components/smartpos/VideoPlayer';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { Image } from '@/components/ui/image';

export default function SectorPage({ sector }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  useDocumentMeta({
    title: sector.metaTitle,
    description: sector.metaDescription,
    canonicalPath: `/${sector.slug}`,
  });

  const goToDemo = () => navigate('/demo');

  return (
    <div className="min-h-screen font-body bg-white">
      <JsonLd data={sector.jsonLd} />
      <Navbar />

      <main id="contenido-principal">
      {/* Hero */}

      <section className="pt-24 pb-16 lg:pt-28 lg:pb-20 bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block text-xs font-black tracking-widest uppercase text-brand-blue mb-4 px-3 py-1 bg-blue-50 rounded-full">
              {sector.eyebrow}
            </span>
            <h1 className="font-heading font-semibold text-4xl lg:text-5xl text-gray-900 mb-5 tracking-tight leading-tight">
              {sector.title} <span className="brand-gradient-text">{sector.titleHighlight}</span>
            </h1>
            <p className="text-gray-500 text-lg leading-relaxed font-light mb-8 max-w-xl">
              {sector.subtitle}
            </p>
            <div className="flex flex-wrap gap-2 mb-8">
              {sector.tags.map((tag) => {
                const Icon = TAG_ICONS[tag] || Check;
                return (
                  <span key={tag} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 text-gray-600 rounded-full text-sm font-medium border border-gray-100">
                    <Icon size={13} className="text-brand-blue" />
                    {tag}
                  </span>
                );
              })}
            </div>
          </div>
          <div className="relative">
            {sector.heroVideo ? (
              <VideoPlayer src={sector.heroVideo} className="rounded-2xl shadow-xl" />
            ) : (
              <div className="rounded-2xl overflow-hidden shadow-xl aspect-[4/3] bg-gray-100">
                <Image src={sector.heroImage} alt={sector.name} fittingType="fill" priority quality={75} className="w-full h-full" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Stats band */}
      <section className="py-6 bg-gray-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {sector.stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-heading font-bold text-3xl lg:text-4xl brand-gradient-text mb-1">{s.value}</div>
              <div className="text-gray-500 text-sm font-light">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Feature blocks */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 space-y-20">
          {sector.blocks.map((b, i) => (
            <div key={i} className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div className={`rounded-2xl overflow-hidden shadow-lg aspect-[4/3] bg-gray-100 ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                <Image src={b.image} alt={b.title} fittingType="fill" quality={75} className="w-full h-full" />
              </div>
              <div>
                <span className="inline-block text-xs font-black tracking-widest uppercase text-brand-blue mb-3">
                  {b.eyebrow}
                </span>
                <h2 className="font-heading font-light text-3xl text-gray-900 mb-4 tracking-tight leading-tight">
                  {b.title}
                </h2>
                <p className="text-gray-500 text-base leading-relaxed font-light mb-6">{b.description}</p>
                {b.bullets && (
                  <ul className="space-y-3">
                    {b.bullets.map((bl) => (
                      <li key={bl.title} className="flex gap-3">
                        <span className="shrink-0 w-5 h-5 rounded-full brand-gradient flex items-center justify-center mt-0.5">
                          <Check size={12} className="text-white" />
                        </span>
                        <div>
                          <span className="font-bold text-gray-900 text-sm">{bl.title}</span>
                          <span className="text-gray-500 text-sm font-light"> — {bl.text}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-light text-3xl lg:text-4xl text-gray-900 leading-tight mb-4 tracking-tight">
            {sector.ctaTitle}
          </h2>
          <p className="text-gray-500 text-lg font-light mb-8">{sector.ctaSubtitle}</p>
          <button
            onClick={goToDemo}
            className="inline-flex items-center gap-2 bg-brand-blue text-white px-8 py-4 rounded-xl font-bold text-sm hover:bg-blue-600 transition-colors"
          >
            {sector.cta || t('sectors.demo')} <ArrowRight size={16} />
          </button>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
}